/**
 * Verlauf eines Vorgangs – der Blick aus „Meine Aufgaben".
 *
 * Zeigt dieselbe Chronik wie die Angebotsmaske, nur gekürzt auf die letzten
 * Einträge. Gebaut wird sie an einer Stelle: Zwei Umsetzungen desselben
 * Verlaufs würden auseinanderlaufen, und dann stünde hier ein Eintrag, den die
 * Angebotsmaske nicht kennt.
 *
 * Nur der neueste Punkt ist farbig. Ältere treten zurück – der Verlauf soll
 * zeigen, wo der Vorgang gerade steht, nicht wie viel schon passiert ist.
 */

import { useNavigate } from 'react-router-dom';
import { Modal, KnopfSekundaer, KnopfPrimaer } from '../komponenten/Modal';
import { chronikBauen, type ChronikArt } from '../hilfen/chronik';
import type { Angebot } from '../typen';
import './verlauf.css';

/** Wie viele Einträge das Fenster zeigt, bevor es auf das Angebot verweist. */
const HOECHSTENS = 8;

const FARBE: Record<ChronikArt, string> = {
  status: 'var(--eh-text-4)',
  mail: 'var(--eh-blau)',
  rueckmeldung: 'var(--eh-gruen)',
  aenderung: 'var(--eh-text-6)',
};

export function VerlaufModal({
  angebot,
  onSchliessen,
}: {
  angebot: Angebot;
  onSchliessen: () => void;
}) {
  const navigate = useNavigate();
  const chronik = chronikBauen(angebot);
  const sichtbar = chronik.slice(0, HOECHSTENS);

  return (
    <Modal
      titel={angebot.name}
      untertitel={`${angebot.kunde.name}${angebot.kunde.ort ? ` · ${angebot.kunde.ort}` : ''}`}
      breite={560}
      zeigeSchliessen
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen}>Schließen</KnopfSekundaer>
          <KnopfPrimaer
            onClick={() => {
              onSchliessen();
              navigate(`/angebot/${angebot.id}`, { state: { herkunft: 'meintag' } });
            }}
          >
            Angebot öffnen
          </KnopfPrimaer>
        </>
      }
    >
      <div className="vl-liste">
        {sichtbar.map((e, i) => (
          <div className="vl-eintrag" key={e.id}>
            <span
              className="vl-punkt"
              style={{ background: i === 0 ? FARBE[e.art] : 'var(--eh-punkt-alt)' }}
            />
            <div className="vl-inhalt">
              <div className="vl-kopf">
                <span className="vl-titel">{e.titel}</span>
                <span className="vl-datum">{e.datumKurz}</span>
              </div>
              <div className="vl-unter">{e.unter}</div>
              {e.notiz && <p className="vl-notiz">{e.notiz}</p>}
            </div>
          </div>
        ))}

        {chronik.length === 0 && (
          <div className="vl-leer">Für diesen Vorgang ist noch nichts festgehalten.</div>
        )}
      </div>

      <p className="vl-fuss">
        Der vollständige Verlauf steht im Angebot – hier die letzten Einträge.
      </p>
    </Modal>
  );
}